/**
 * 07 · Back from checkout.
 *
 * Shown when the browser returns from Polar without a completed payment. The
 * answer to "I got to the card form and stopped" is not a bigger discount, it
 * is removing the thing that stopped them: nothing is charged today.
 *
 * The quiz, the result and the paywall position are all still here — this
 * screen sits on top of the paywall rather than replacing it, so dismissing it
 * returns the person exactly where they were.
 */
import React from 'react';
import { Icon } from '../app/icons';
import { c, hair, f, ACT } from '../app/tokens';
import { Col, Meta, Muted, Row, Appear } from '../app/primitives';
import { useTt } from '../i18n/useT';
import { REFUND_DAYS, TRIAL_DAYS } from '../config/paywall';
import { Cta, OK, Screen, Serif, SoftLink, StickyActions, useWide } from './ui';

const POINTS = ['pw.rec.1', 'pw.rec.2', 'pw.rec.3', 'pw.rec.4'];

const ZeroCard: React.FC<{ wide?: boolean }> = ({ wide }) => {
    const { t } = useTt();

    return (
        <div
            style={{
                border: `1px solid ${OK}3d`,
                background: `linear-gradient(170deg, ${OK}12, ${c.card} 62%)`,
                borderRadius: 20,
                padding: wide ? '34px 26px 30px' : '26px 20px 22px',
                textAlign: 'center',
            }}
        >
            <Row style={{ justifyContent: 'center', alignItems: 'baseline', gap: wide ? 12 : 10 }}>
                <span
                    style={{
                        fontFamily: f.mono,
                        fontSize: wide ? 68 : 58,
                        fontWeight: 200,
                        letterSpacing: '-0.055em',
                        color: c.textPri,
                        lineHeight: 1,
                    }}
                >
                    $0
                </span>
                <Meta style={{ fontSize: wide ? 13 : 12, color: OK, letterSpacing: '0.14em' }}>
                    {t('pw.rec.today')}
                </Meta>
            </Row>
            <div
                style={{
                    fontSize: wide ? 17 : 16,
                    color: c.textPri,
                    fontWeight: 400,
                    letterSpacing: '-0.018em',
                    marginTop: wide ? 15 : 13,
                }}
            >
                {t('pw.rec.access', { n: TRIAL_DAYS })}
            </div>
        </div>
    );
};

const Checklist: React.FC<{ wide?: boolean }> = ({ wide }) => {
    const { t } = useTt();

    return (
        <Col gap={wide ? 12 : 11}>
            {POINTS.map((k) => (
                <Row key={k} style={{ gap: 12, alignItems: 'flex-start' }}>
                    <Icon k="check" size={wide ? 14 : 13} color={OK} style={{ marginTop: 2, flex: '0 0 auto' }} />
                    <span style={{ fontSize: wide ? 14 : 13.5, color: c.text, fontWeight: 300, lineHeight: 1.45 }}>
                        {t(k)}
                    </span>
                </Row>
            ))}
        </Col>
    );
};

interface Props {
    /** Take the offer — hands the person back to the paywall, not to checkout. */
    onAccept: () => void;
    onDismiss: () => void;
}

const Recovery: React.FC<Props> = ({ onAccept, onDismiss }) => {
    const { t } = useTt();
    const wide = useWide();
    const note = t('pw.rec.note', { n: REFUND_DAYS });

    if (wide) {
        return (
            <Screen wide style={{ padding: '38px 40px 56px' }}>
                <Appear>
                    <Row style={{ gap: 36, maxWidth: 1000, margin: '20px auto 0', alignItems: 'center' }}>
                        <Col style={{ flex: 1, minWidth: 0 }} gap={16}>
                            <Meta style={{ fontSize: 10, color: ACT, letterSpacing: '0.18em' }}>
                                {t('pw.rec.eyebrow')}
                            </Meta>
                            <Serif size={40} style={{ lineHeight: 1.14 }}>
                                {t('pw.rec.title')}
                            </Serif>
                            <Muted style={{ fontSize: 15, lineHeight: 1.55 }}>
                                {t('pw.rec.sub', { n: TRIAL_DAYS })}
                            </Muted>
                            <div style={{ marginTop: 6 }}>
                                <Checklist wide />
                            </div>
                        </Col>
                        <Col style={{ width: 400, flex: '0 0 auto' }} gap={18}>
                            <ZeroCard wide />
                            <Cta
                                label={t('pw.rec.cta', { n: TRIAL_DAYS })}
                                note={note}
                                onClick={onAccept}
                                width={400}
                                height={54}
                            />
                            <SoftLink label={t('pw.rec.later')} onClick={onDismiss} />
                        </Col>
                    </Row>
                </Appear>
            </Screen>
        );
    }

    return (
        <Screen column>
            <Appear style={{ flex: 1, minHeight: 0 }}>
                <div style={{ padding: '40px 22px 0', textAlign: 'center' }}>
                    <div
                        style={{
                            width: 50,
                            height: 50,
                            borderRadius: '50%',
                            border: hair,
                            margin: '0 auto',
                            display: 'grid',
                            placeItems: 'center',
                            background: c.card,
                        }}
                    >
                        <Icon k="clock" size={21} color={c.muted} />
                    </div>
                    <Meta
                        style={{ display: 'block', fontSize: 9.5, color: ACT, letterSpacing: '0.16em', marginTop: 17 }}
                    >
                        {t('pw.rec.eyebrow')}
                    </Meta>
                    <Serif size={25} style={{ marginTop: 11, textAlign: 'center', lineHeight: 1.2 }}>
                        {t('pw.rec.title')}
                    </Serif>
                    <Muted style={{ fontSize: 13, marginTop: 10 }}>{t('pw.rec.sub', { n: TRIAL_DAYS })}</Muted>
                </div>

                <div style={{ margin: '24px 20px 0' }}>
                    <ZeroCard />
                </div>

                <div style={{ padding: '20px 20px 0' }}>
                    <Checklist />
                </div>
            </Appear>

            <StickyActions>
                <Cta label={t('pw.rec.cta', { n: TRIAL_DAYS })} note={note} onClick={onAccept} />
                <SoftLink label={t('pw.rec.later')} onClick={onDismiss} />
            </StickyActions>
        </Screen>
    );
};

export default Recovery;
